// -----------------------------------------------------------------------------
// src/components/dashboard/views/CompaniesView.js          2025-06-30
// Fetches companies on-mount via client.users.fetchSupervisedCompanies()
// -----------------------------------------------------------------------------
import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useQuery } from 'react-query';

import { renderAvatar } from './../dashboardUtils';
import client from '../../../API';

/* ---------- tiny wrapper for the tab content ---------- */
const TabPanel = ({ children, value, index }) =>
  value === index ? <Box sx={{ p: 2 }}>{children}</Box> : null;

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */
export default function CompaniesView({ searchQuery = '' }) {
  /* ───────── fetch supervised companies ───────── */
  const {
    data: items = [],
    isLoading,
    error: fetchError,
  } = useQuery(
    'companies',
    () => client.users.fetchSupervisedCompanies(),
    { staleTime: 60_000 }
  );

  /* ───────── search filter ───────── */
  const visible = items.filter((co) =>
    co.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* ───────── dialog state ───────── */
  const [openId, setOpenId] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [projectFilter, setProjectFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  /* ───────── handlers ───────── */
  const handleOpen = async (company) => {
    setOpenId(company.company_id);
    setLoading(true);
    setError(null);
    try {
      const data = await client.companies.activityReport(company.company_id);
      setReport(data);
      setTab(0);
      setProjectFilter('All');
      setStatusFilter('All');
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpenId(null);
    setReport(null);
    setError(null);
    setTab(0);
    setProjectFilter('All');
    setStatusFilter('All');
  };

  /* ───────── loading / fetch-error ───────── */
  if (isLoading) return <CircularProgress sx={{ m: 4 }} />;
  if (fetchError)
    return (
      <Alert severity="error" sx={{ m: 4 }}>
        {fetchError.message || 'Failed to load companies'}
      </Alert>
    );

  /* ───────── render ───────── */
  return (
    <>
      {/* ---------- grid of company cards ---------- */}
      <Grid container spacing={3}>
        {visible.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">No companies found.</Alert>
          </Grid>
        )}

        {visible.map((item) => (
          <Grid key={item.company_id} item xs={12} sm={6} md={4} lg={3}>
            <Card
              onClick={() => handleOpen(item)}
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                {renderAvatar(item, 'companies')}
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {item.company_name}
                </Typography>
                <Chip
                  label="Company"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ---------- drill-down dialog ---------- */}
      <Dialog
        open={Boolean(openId)}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{report?.company_name || 'Loading…'}</DialogTitle>

        <DialogContent dividers>
          {loading && (
            <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />
          )}

          {error && (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && report && (
            <>
              {/* ---------- filters ---------- */}
              {(tab === 0 || tab === 1) && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                  }}
                >
                  {tab === 0 && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Project Type</InputLabel>
                      <Select
                        label="Project Type"
                        value={projectFilter}
                        onChange={(e) => setProjectFilter(e.target.value)}
                      >
                        <MenuItem value="All">All</MenuItem>
                        {Array.from(
                          new Set(report.activity.map((r) => r.revenue_type))
                        ).map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="All">All</MenuItem>
                      {tab === 0 &&
                        Array.from(
                          new Set(report.activity.map((r) => r.status))
                        ).map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}

                      {tab === 1 &&
                        Array.from(
                          new Set(report.payments.map((p) => p.status))
                        ).map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* ---------- extra invoice filter ---------- */}
              {tab === 1 && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Invoice Status</InputLabel>
                    <Select
                      label="Invoice Status"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="All">All</MenuItem>
                      {Array.from(
                        new Set(report.invoices.map((i) => i.status))
                      ).map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* ---------- tabs ---------- */}
              <Tabs
                value={tab}
                onChange={(_, v) => {
                  setTab(v);
                  setProjectFilter('All');
                  setStatusFilter('All');
                }}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab label="Activity" />
                <Tab label="Invoices" />
              </Tabs>

              {/* ---------- Activity Tab ---------- */}
              <TabPanel value={tab} index={0}>
                <List>
                  {report.activity
                    .filter(
                      (row) =>
                        (projectFilter === 'All' ||
                          row.revenue_type === projectFilter) &&
                        (statusFilter === 'All' || row.status === statusFilter)
                    )
                    .map((row, i) => (
                      <React.Fragment key={i}>
                        <ListItem>
                          <ListItemText
                            primary={`${row.revenue_type.toUpperCase()}: ${row.revenue_name}`}
                            secondary={
                              <>
                                <strong>Date:</strong>{' '}
                                {new Date(
                                  row.date_key
                                ).toLocaleDateString()}
                                <br />
                                <strong>Amount:</strong>{' '}
                                ${row.revenue_amount.toFixed(2)}
                                <br />
                                <strong>Status:</strong> {row.status}
                              </>
                            }
                          />
                        </ListItem>
                        {i < report.activity.length - 1 && (
                          <Divider component="li" />
                        )}
                      </React.Fragment>
                    ))}
                </List>
              </TabPanel>

              {/* ---------- Invoices Tab ---------- */}
              <TabPanel value={tab} index={1}>
                <List>
                  {report.invoices
                    .filter(
                      (inv) => statusFilter === 'All' || inv.status === statusFilter
                    )
                    .map((inv, i) => (
                      <React.Fragment key={i}>
                        <ListItem>
                          <ListItemText
                            primary={`Invoice #${inv.id} — ${
                              inv.po_number || 'N/A'
                            }`}
                            secondary={
                              <>
                                <strong>Amount Due:</strong>{' '}
                                ${parseFloat(inv.amount_due).toFixed(2)}
                                <br />
                                <strong>Created:</strong>{' '}
                                {new Date(inv.created_at).toLocaleDateString()}
                                <br />
                                <strong>Status:</strong> {inv.status}
                                <br />
                                <strong>Days Late:</strong> {inv.days_late}
                              </>
                            }
                          />
                        </ListItem>
                        {i < report.invoices.length - 1 && (
                          <Divider component="li" />
                        )}
                      </React.Fragment>
                    ))}
                </List>
              </TabPanel>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
